import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { debugZodResolver } from '@/lib/debugZodResolver.ts';
import {
  tagSchema,
  TagFormData,
  TagFormInput,
  tagDefaultValues,
} from '@/features/dashboard/tags/schemas/tagSchema.ts';
import { tagMapper } from '@/features/dashboard/tags/types/TagWebDto.mapper.ts';
import { getRandomTagColor } from '@/features/dashboard/tags/types/tagColor.utils.ts';
import useCreateTag from '@/features/dashboard/tags/hooks/useCreateTag.ts';
import TagForm from '@/features/dashboard/tags/components/TagForm.tsx';

const CreateTagModal = ({ onClose }: { onClose: () => void }) => {
  const createMutation = useCreateTag();

  // Start each new tag with a random color from the palette.
  const defaultValues = useMemo<TagFormInput>(
    () => ({ ...tagDefaultValues, color: getRandomTagColor() }),
    [],
  );

  const methods = useForm<TagFormInput>({
    resolver: debugZodResolver(tagSchema),
    defaultValues,
  });

  const onSubmit = (data: TagFormData) => {
    createMutation.mutate(tagMapper.fromFormToDto(data), {
      onSuccess: () => {
        toast.success('Tag został utworzony');
        onClose();
      },
    });
  };

  return (
    <Modal
      onClose={onClose}
      isDirty={methods.formState.isDirty}
      className="max-w-[520px]"
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <ModalHeader title="Dodaj tag" onClose={onClose} />
          <ModalContent>
            <TagForm />
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={createMutation.isPending}
            >
              Dodaj tag
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default CreateTagModal;
